import type { ImageBleed, PageType } from 'types';
import type { ProductListItems, ProductListModule } from 'types/capi/article';

import ProductCard from '~/components/ProductCard';
import getHexBrightness from '~/utils/hexBrightness';

import ProductListBlock from './ProductListBlock';

const MODULE_TYPE = 'product-list';

export default function ProductList(
  props: ProductListModule & {
    bleed?: ImageBleed;
    columnCount?: number;
    modulePosition?: number;
    pageType: PageType;
  }
) {
  const items = props.items.filter(
    (item): item is NonNullable<typeof item> => item != null
  );

  if (!items.length) return null;

  switch (props.display_type) {
    case 'carousel':
      return (
        <ProductListCarousel
          {...props}
          items={items}
          modulePosition={props.modulePosition}
          pageType={props.pageType}
        />
      );
    case 'grid':
      return (
        <ProductListGrid
          {...props}
          items={items}
          modulePosition={props.modulePosition}
          pageType={props.pageType}
        />
      );
    case 'block':
      return (
        <ProductListBlock
          backgroundImage={props.background_image}
          backgroundColor={props.background_colour}
          bleed={props.bleed}
          columnCount={props.columnCount}
          modulePosition={props.modulePosition}
          product={items[0]!}
          pageType={props.pageType}
        />
      );
    default:
      return null;
  }
}

function useListImageRatio(items: ProductListItems) {
  return items.some((item) => item.image?.ratio === '4:5')
    ? ('4:5' as const)
    : undefined;
}

function ProductListCarousel({
  header,
  description,
  items,
  modulePosition,
  background_colour = '#FAFAFA',
  pageType
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListItems;
  modulePosition?: number;
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const listRatio = useListImageRatio(items);

  return (
    <div
      className={`col-span-full w-full ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="content-width flex h-fit flex-col items-center gap-8">
        {(header || description) && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            {header && <div className="text-title-lg">{header}</div>}
            {description && <div className="text-sm">{description}</div>}
          </div>
        )}
        <div
          className={`carousel-width no-scrollbar flex shrink-0 justify-start gap-2 md:gap-6 ${
            shouldCentreItems
              ? 'md:justify-center md:overflow-x-visible'
              : 'md:justify-start'
          }`}
        >
          {items.map((product, productIndex) => {
            const className =
              'h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]';

            if (product.type === 'inline-external') {
              return (
                <ProductCard.InlineExternal
                  cardSize="small"
                  name={`PRODUCT_CARD_${product.name}`}
                  className={className}
                  imageLoading="eager"
                  listRatio={listRatio}
                  key={product.url}
                  padImage
                  pagePosition="Product Carousel"
                  product={product}
                />
              );
            }

            return product.type === 'external' ? (
              <ProductCard.External
                allowNotifications
                cardSize="small"
                name={`PRODUCT_CARD_${product.name}`}
                className={className}
                imageLoading="eager"
                listRatio={listRatio}
                key={product.id}
                modulePosition={modulePosition}
                moduleType={MODULE_TYPE}
                openPdp={false}
                padImage
                pagePosition="Product Carousel"
                position={productIndex + 1}
                product={product}
                pageType={pageType}
              />
            ) : (
              <ProductCard.Internal
                allowNotifications
                cardSize="small"
                name={`PRODUCT_CARD_${header}`}
                className={className}
                imageLoading="eager"
                listRatio={listRatio}
                key={product.id}
                modulePosition={modulePosition}
                moduleType={MODULE_TYPE}
                padImage
                pageType={pageType}
                pagePosition="Product Carousel"
                position={productIndex + 1}
                product={product}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProductListGrid({
  header,
  description,
  items,
  modulePosition,
  background_colour = '#2A2528',
  pageType
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListItems;
  modulePosition?: number;
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const listRatio = useListImageRatio(items);

  const columnClass = shouldCentreItems
    ? `md:grid-cols-${items.length}`
    : 'lg:grid-cols-4';

  return (
    <div
      className={`w-full ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="content-width flex flex-col items-center gap-6 max-md:px-0">
        {(header || description) && (
          <div className="flex flex-col items-center gap-2 text-center">
            {header && <h2 className="text-title-lg">{header}</h2>}
            {description && (
              <p
                className={`text-sm ${bgBrightness === 'dark' ? 'text-gray-300' : 'text-black'}`}
              >
                {description}
              </p>
            )}
          </div>
        )}
        <div
          className={`w-full ${shouldCentreItems ? 'flex justify-center' : ''}`}
        >
          <div
            className={`grid grid-cols-2 ${columnClass} gap-x-2 gap-y-4 bg-inherit md:gap-6`}
          >
            {items.map((product, productIndex) => {
              const className = 'mx-auto h-auto shrink-0';

              switch (product.type) {
                case 'internal':
                  return (
                    <ProductCard.Internal
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${header}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.id}
                      modulePosition={modulePosition}
                      moduleType={MODULE_TYPE}
                      padImage
                      pageType={pageType}
                      pagePosition="Product Grid"
                      position={productIndex + 1}
                      product={product}
                    />
                  );
                case 'external':
                  return (
                    <ProductCard.External
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.id}
                      modulePosition={modulePosition}
                      moduleType={MODULE_TYPE}
                      openPdp={false}
                      padImage
                      pagePosition="Product Grid"
                      position={productIndex + 1}
                      product={product}
                      pageType={pageType}
                    />
                  );
                case 'inline-external':
                  return (
                    <ProductCard.InlineExternal
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.url}
                      padImage
                      pagePosition="Product Grid"
                      product={product}
                    />
                  );
                default:
                  return <></>;
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
